import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { WorkAuthorCreateStore } from "../../store";
import { runInAction } from "mobx";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { AuthorComposerCreate } from "../../../authorComposer/components/create/AuthorComposerCreate";
import { AuthorComposerCreateStore } from "../../../authorComposer/store";
import { InputNumber } from "../../../common/controls";
import { ItemsCRUDCompact } from "../../../common/controls";
import { CrudRow } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { WorkAuthorPublisherCreate } from "../../../workAuthorPublisher/components/create/WorkAuthorPublisherCreate";
import { WorkAuthorPublisherCreateStore } from "../../../workAuthorPublisher/store";
import { GridColumn } from "../../../common/types";
import { FormControl } from "@mui/material";

export type WorkAuthorCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  authorSelectedValue?: { displayId: string; id: string } | null;
  onAuthorSelected?: (value: { displayId: string; id: string } | null) => void;
  onAuthorChange?: (value: string) => void;
  authorOptions?: { displayId: string; id: string }[];
  authorError?: string;
  viewFormAuthor?: () => void;
  lyricsPercentageSelectedValue?: { displayId: string; id: string } | null;
  onLyricsPercentageChange?: (value: number) => void;
  lyricsPercentage: number;
  lyricsPercentageError?: string;
  musicPercentageSelectedValue?: { displayId: string; id: string } | null;
  onMusicPercentageChange?: (value: number) => void;
  musicPercentage: number;
  musicPercentageError?: string;
  publishersOnRestore: (id: string) => void;
  publishersOnCommitChanges: (changes: GridChangeSet) => void;
  publishersValidateChanges: (changes: GridChangeSet) => boolean;
  publishersRows: CrudRow[];
  workAuthorPublisherCreateStore: WorkAuthorPublisherCreateStore;
  publishersError?: string;
  publishersVisibleColumns: GridColumn[];
  dummyCRUDRestoredObservable: number;
  workAuthorCreateStore: WorkAuthorCreateStore;
};

export const WorkAuthorCreateForm: React.FC<WorkAuthorCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onAuthorSelected = () => {},
      authorSelectedValue = null,
      authorOptions = [],
      authorError,
      onAuthorChange = () => {},
      viewFormAuthor = () => {},
      lyricsPercentage = 0,
      lyricsPercentageError,
      onLyricsPercentageChange = () => {},
      musicPercentage = 0,
      musicPercentageError,
      onMusicPercentageChange = () => {},
      publishersOnRestore,
      publishersOnCommitChanges,
      publishersValidateChanges,
      publishersRows,
      workAuthorPublisherCreateStore,
      publishersError,
      publishersVisibleColumns,
      dummyCRUDRestoredObservable,
      workAuthorCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      if (workAuthorPublisherCreateStore.status === "uninitialized") {
        workAuthorPublisherCreateStore.initialize();
      }

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="WorkAuthor"
              mode="create"
              values={{}}
              options={{ author: authorOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "author":
                    onAuthorSelected?.(valor);
                    break;
                  case "lyricsPercentage":
                    onLyricsPercentageChange?.(valor);
                    break;
                  case "musicPercentage":
                    onMusicPercentageChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={workAuthorCreateStore.getCardErrors("null")}
              title={langStore.groupWorkAuthorLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="author"
                  label={langStore.WorkAuthorAuthorLabel}
                  autoFocus
                  error={Boolean(authorError)}
                  helperText={
                    authorError ||
                    workAuthorCreateStore.warnings["author"]?.message
                  }
                  selectedValue={authorSelectedValue}
                  onChange={(value: string) => onAuthorChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onAuthorSelected(value)
                  }
                  options={authorOptions}
                  description={langStore.WorkAuthorAuthorDescription}
                  onView={viewFormAuthor}
                  canCreate={rootStore.authStore.hasPermissions(
                    "authorComposer_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new AuthorComposerCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <AuthorComposerCreate
                          authorComposerCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewAuthorComposer"
                      cancelMethod="cancelNewAuthorComposer"
                    />
                  )}
                />
                <InputNumber
                  value={lyricsPercentage}
                  error={Boolean(lyricsPercentageError)}
                  warning={
                    !lyricsPercentageError &&
                    Boolean(workAuthorCreateStore.warnings["lyricsPercentage"])
                  }
                  helperText={
                    lyricsPercentageError ||
                    workAuthorCreateStore.warnings["lyricsPercentage"]?.message
                  }
                  allowNegative={true}
                  label={langStore.WorkAuthorLyricsPercentageLabel}
                  decimalScale={2}
                  description={langStore.WorkAuthorLyricsPercentageDescription}
                  onChange={(value: number) => onLyricsPercentageChange(value)}
                />
                <InputNumber
                  value={musicPercentage}
                  error={Boolean(musicPercentageError)}
                  warning={
                    !musicPercentageError &&
                    Boolean(workAuthorCreateStore.warnings["musicPercentage"])
                  }
                  helperText={
                    musicPercentageError ||
                    workAuthorCreateStore.warnings["musicPercentage"]?.message
                  }
                  allowNegative={true}
                  label={langStore.WorkAuthorMusicPercentageLabel}
                  decimalScale={2}
                  description={langStore.WorkAuthorMusicPercentageDescription}
                  onChange={(value: number) => onMusicPercentageChange(value)}
                />
                <ItemsCRUDCompact
                  label={langStore.WorkAuthorPublishersLabel}
                  description={langStore.WorkAuthorPublishersDescription}
                  showLabel={true}
                  rows={publishersRows}
                  columns={publishersVisibleColumns}
                  popupComponent={(row: any) => {
                    if (
                      row &&
                      row.id &&
                      !(
                        typeof row.id === "string" && row.id.startsWith("auto-")
                      )
                    ) {
                      runInAction(() =>
                        workAuthorCreateStore.publishersLoadRow(row)
                      );
                    }
                    return (
                      <WorkAuthorPublisherCreate
                        workAuthorPublisherCreateStore={
                          workAuthorPublisherCreateStore
                        }
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    );
                  }}
                  popupMaxWidth="sm"
                  onRestore={(id: string) => publishersOnRestore(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    publishersOnCommitChanges(changes)
                  }
                  validate={(changes: GridChangeSet) =>
                    publishersValidateChanges(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={publishersError}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
